import { useMemo } from 'react';
import { useAtlasV2ApplyEntityFilters } from 'src/components/AtlasV2Components/Hooks/useAtlasV2BaseMetricRequestBuilder';
import { useAdvancedSearchRequestBuilder } from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useAppSelector, useAvailableTImePeriod, useLastYearAvailableTimePeriod } from 'src/utils/Hooks';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';

export const useAtlasV2MarketShareHooks = (fieldName = 'retailSales') => {
  const retailerId = useRetailerId();
  const { startWeek, endWeek, id: mainTimePeriodId } = useAppSelector((state) => state.mainTimePeriod);

  const { startAndEndWeeks } = useAvailableTImePeriod();
  const lastYearStartAndWeeks = useLastYearAvailableTimePeriod();
  const marketShareRequestId = 'marketShareGrid';
  const marketShareLastYearRequestId = 'marketShareGrid-lastYear';
  const displayName = fieldName === 'retailSales' ? 'Retail Sales' : 'Units Sold';
  const entityType = useAppSelector((state) => state?.entityService?.mainEntity?.type);

  const applyEntityFilters = useAtlasV2ApplyEntityFilters();

  // Aggregation Request for specific categories (groupBy BrandId) - of OF CURRENT MARKET SHARE // LAST YEAR WILL BE THERE for chart export
  const aggregationBrandRetailSales = new AggregationBuilder('brandId')
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .addAggregationField(displayName, fieldName, 'sum', true)
    .addConditionTermFilter('retailerId', [retailerId]);

  if (mainTimePeriodId === 'cd') {
    aggregationBrandRetailSales.addComparisonRangeFilter('weekId', startWeek, endWeek);
  }

  startAndEndWeeks.forEach((period) =>
    aggregationBrandRetailSales.addComparisonRangeFilter('weekId', period[0], period[1])
  );

  // Main Request for specific categories // OF CURRENT MARKET SHARE
  const clientRequestBuilder = useAdvancedSearchRequestBuilder(`${marketShareRequestId}-brands`, 'atlas-sales');

  clientRequestBuilder
    .setPageNumber(1)
    .setPageSize(30)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setSearchBy('parent')
    .addAggregation(aggregationBrandRetailSales.build())
    .addSortField(fieldName, fieldName, 'sum', false)
    .apply(applyEntityFilters);

  if (entityType !== 'segment') {
    clientRequestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
  }

  const clientRequest = clientRequestBuilder.build();

  //  aggregation Request for specific categories (for market) (groupBy RetailId)
  const aggregationRetailerIdRetailSales = new AggregationBuilder('retailerId')
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .addAggregationField(displayName, fieldName, 'sum', true)
    .addConditionTermFilter('retailerId', [retailerId]);

  if (mainTimePeriodId === 'cd') {
    aggregationRetailerIdRetailSales.addComparisonRangeFilter('weekId', startWeek, endWeek);
  }

  startAndEndWeeks.forEach((period) =>
    aggregationRetailerIdRetailSales.addComparisonRangeFilter('weekId', period[0], period[1])
  );

  // Main Request for specific categories
  const marketRequestBuilder = useAdvancedSearchRequestBuilder(`${marketShareRequestId}-market`, 'atlas-sales');

  marketRequestBuilder
    .setPageNumber(1)
    .setPageSize(30)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .removeConditionTermFilters((termFilter) => termFilter.fieldName === 'retailerId')
    .removeConditionTermFilters((termFilter) => termFilter.fieldName === 'categoryId')
    .setRetailerId(retailerId)
    .setSearchBy('parent')
    .addAggregation(aggregationRetailerIdRetailSales.build())
    .addSortField(fieldName, fieldName, 'sum', false)
    .apply(applyEntityFilters);

  if (entityType !== 'segment') {
    marketRequestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
  }

  const marketRequest = marketRequestBuilder.build();

  // LAST YEAR - required for export csv
  const lastYearAggregationBrandRetailSales = new AggregationBuilder('brandId')
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .addAggregationField(displayName, fieldName, 'sum', true)
    .addConditionTermFilter('retailerId', [retailerId]);

  lastYearStartAndWeeks.forEach((period) =>
    lastYearAggregationBrandRetailSales.addComparisonRangeFilter('weekId', period[0], period[1])
  );

  // Main Request for specific categories // OF Compare MARKET SHARE
  const lastYearClientRequestBuilder = useAdvancedSearchRequestBuilder(
    `${marketShareLastYearRequestId}-brands`,
    'atlas-sales'
  );

  lastYearClientRequestBuilder
    .setPageNumber(1)
    .setPageSize(30)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .setRetailerId(retailerId)
    .setSearchBy('parent')
    .addAggregation(lastYearAggregationBrandRetailSales.build())
    .addSortField(fieldName, fieldName, 'sum', false)
    .apply(applyEntityFilters);
  // .removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');

  if (entityType !== 'segment') {
    lastYearClientRequestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
  }

  const lastYearClientRequest = lastYearClientRequestBuilder.build();

  //  last year aggregation Request for specific categories (for market) (groupBy RetailId)
  const lastYearAggregationRetailerIdRetailSales = new AggregationBuilder('retailerId')
    .addConditionRangeFilter('weekId', startWeek, endWeek)
    .addAggregationField(displayName, fieldName, 'sum', true)
    .addConditionTermFilter('retailerId', [retailerId]);

  lastYearStartAndWeeks.forEach((period) =>
    lastYearAggregationRetailerIdRetailSales.addComparisonRangeFilter('weekId', period[0], period[1])
  );

  // Main Request for specific categories
  const lastYearMarketRequestBuilder = useAdvancedSearchRequestBuilder(
    `${marketShareLastYearRequestId}-market`,
    'atlas-sales'
  );

  lastYearMarketRequestBuilder
    .setPageNumber(1)
    .setPageSize(30)
    .setPeriod('year')
    .setDoAggregation(true)
    .setReturnDocuments(false)
    .removeConditionTermFilters((termFilter) => termFilter.fieldName === 'retailerId')
    .removeConditionTermFilters((termFilter) => termFilter.fieldName === 'categoryId')
    .setRetailerId(retailerId)
    .setSearchBy('parent')
    .addAggregation(lastYearAggregationRetailerIdRetailSales.build())
    .addSortField(fieldName, fieldName, 'sum', false)
    .apply(applyEntityFilters);

  if (entityType !== 'segment') {
    lastYearMarketRequestBuilder.removeConditionTermFilters((termFilter) => termFilter.fieldName === 'brandId');
  }

  const lastYearMarketRequest = lastYearMarketRequestBuilder.build();

  const { data, isLoading } = useGenericAdvancedSearch({
    requestId: marketShareRequestId,
    queryKeys: [clientRequestBuilder, marketRequest, lastYearClientRequest, lastYearMarketRequest],
    requestBody: [clientRequest, marketRequest, lastYearClientRequest, lastYearMarketRequest]
  });

  const parsedData = useMemo(() => {
    const totalMarketValues = data?.data?.[1]?.aggregations.by_retailerId?.[0]?.additionalValues ?? [];
    const lastYearMarketValues = data?.data?.[3]?.aggregations.by_retailerId?.[0]?.additionalValues ?? [];
    const rawBrandsValues = data?.data?.[0]?.aggregations.by_brandId ?? [];
    const lastYearRawBrandsValues = data?.data?.[2]?.aggregations.by_brandId ?? [];
    const valuesByBrandMap = new Map();
    const lastYearValuesByBrandMap = new Map();

    rawBrandsValues.forEach((brand) => {
      valuesByBrandMap.set(brand.fieldId, { ...brand.additionalValues, ...brand.additionalMetaData });
    });

    lastYearRawBrandsValues.forEach((brand) => {
      lastYearValuesByBrandMap.set(brand.fieldId, { ...brand.additionalValues, ...brand.additionalMetaData });
    });

    return { totalMarketValues, valuesByBrandMap, lastYearMarketValues, lastYearValuesByBrandMap };
  }, [data]);

  return { parsedData, isLoading };
};
